import { LDProvider } from "launchdarkly-react-client-sdk";
import { observer } from "mobx-react-lite";
import { ReactNode, useMemo } from "react";
import { ENV_VARS } from "../../../config/env.ts";
import { BRIA_ORG_ID } from "../../../constants/OrgConstants";
import { getSelectedOrganization } from "../../../helpers/localStorage";
import { useAppStore } from "../../../hooks/useStores";

interface Props {
	children: ReactNode;
}

const buildLaunchDarklyContext = (userEmail: string, selectedOrgId?: string) => {
	const userCtx = {
		kind: "user",
		key: userEmail.toLowerCase().includes("@bria.ai") ? "bria" : "non-bria",
		email: userEmail,
		anonymous: false,
	};

	const orgCtx = selectedOrgId && {
		kind: "organization",
		key: selectedOrgId === BRIA_ORG_ID ? "bria" : "non-bria",
		org_id: selectedOrgId,
		anonymous: false,
	};

	return orgCtx ? { kind: "multi", user: userCtx, organization: orgCtx } : userCtx;
};

const LaunchDarklyProviderInner = ({ children }: Props) => {
	const clientId = ENV_VARS.VITE_PUBLIC_LD_CLIENT_SIDE_ID as string | undefined;
	const { authStore } = useAppStore();

	const selectedOrgId = getSelectedOrganization()?.organization?.uid;
	const userEmail = authStore.user?.email;

	// Build context only when we have authenticated user with email
	const context = useMemo(() => {
		if (!userEmail) {
			return undefined;
		}
		return buildLaunchDarklyContext(userEmail, selectedOrgId);
	}, [userEmail, selectedOrgId]);

	// Do not initialize LD without a real authenticated user with known email
	// Using deferInitialization to prevent anonymous user creation
	if (!clientId || !userEmail || !context) {
		return <>{children}</>;
	}

	return (
		<LDProvider
			clientSideID={clientId}
			context={context}
			deferInitialization={true}
			reactOptions={{ useCamelCaseFlagKeys: false }}
		>
			{children}
		</LDProvider>
	);
};

export const LaunchDarklyProvider = observer(LaunchDarklyProviderInner);
export default LaunchDarklyProvider;
